<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getNaturalLanguage,
  getRelativeTime,
  getExactTime,
  parseEventKey,
  type EventRecord,
  type ActivityQueryParams,
} from '@bono-packages/activity-log';
import { useActivityLogStore } from '@/stores/activityLog';
import { useCompanyStore } from '@/stores/company';
import { hasSelectedCompany } from '@/utils/companyContext';

const { t } = useI18n();
const activityLogStore = useActivityLogStore();
const companyStore = useCompanyStore();

// Compact, read-only recent-activity card for the company dashboard. It renders a simplified
// list (operation, who, when) — no company name, since everything shown already belongs to the
// selected organization (scope enforced server-side). Full filtering lives on the detail page.
const RECENT_LIMIT = 10;

const events = ref<EventRecord[]>([]);
const loading = ref(false);

// Remixicon-based icon per event type. Kept local (not the package's getIconForEventType) so
// the card renders correct `ri-*` classes regardless of the installed package version.
const iconForEventType = (eventType?: string): { icon: string; color: string; bg: string } => {
  const type = (eventType || '').toLowerCase();
  if (type.includes('create') || type.includes('add'))
    return { icon: 'ri-add-line', color: 'text-green-600', bg: 'bg-green-100' };
  if (type.includes('update') || type.includes('edit') || type.includes('change'))
    return { icon: 'ri-edit-box-line', color: 'text-blue-600', bg: 'bg-blue-100' };
  if (type.includes('delete') || type.includes('remove'))
    return { icon: 'ri-delete-bin-5-line', color: 'text-red-600', bg: 'bg-red-100' };
  if (type.includes('upload') || type.includes('import'))
    return { icon: 'ri-upload-2-line', color: 'text-purple-600', bg: 'bg-purple-100' };
  if (type.includes('calculat'))
    return { icon: 'ri-calculator-line', color: 'text-orange-600', bg: 'bg-orange-100' };
  if (type.includes('complete') || type.includes('success') || type.includes('finish'))
    return { icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-100' };
  if (type.includes('fail') || type.includes('error'))
    return { icon: 'ri-close-circle-line', color: 'text-red-600', bg: 'bg-red-100' };
  if (type.includes('mapping'))
    return { icon: 'ri-node-tree', color: 'text-indigo-600', bg: 'bg-indigo-100' };
  if (type.includes('standard'))
    return { icon: 'ri-settings-3-line', color: 'text-cyan-600', bg: 'bg-cyan-100' };
  return { icon: 'ri-file-line', color: 'text-gray-600', bg: 'bg-gray-100' };
};

// Derive a compact view-model per event so the template stays declarative.
const toCard = (event: EventRecord) => {
  const date = event.occurredAt ?? event.createdAt;
  const { eventType } = parseEventKey(event.eventKey || '');
  return {
    id: event.id,
    operation: getNaturalLanguage(event),
    user: event.userName || event.userEmail || '—',
    relative: date ? getRelativeTime(date) : '',
    exact: date ? getExactTime(date) : '',
    ...iconForEventType(eventType),
  };
};

const cards = computed(() => events.value.map(toCard));

const fetchRecent = async () => {
  if (!hasSelectedCompany()) return;
  loading.value = true;
  try {
    const params: ActivityQueryParams = {
      language: localStorage.getItem('locale') || 'en',
      includeConsumerStatuses: false,
      limit: RECENT_LIMIT,
      offset: 0,
      parentsOnly: true,
    };
    const result = await activityLogStore.fetchEvents(params);
    events.value = result.events;
  } catch (err) {
    console.error('[CompanyActivityCard] Failed to fetch events:', err);
    events.value = [];
  } finally {
    loading.value = false;
  }
};

// On fresh login the company is selected after this component mounts, so watch the store's
// companyId (immediate) rather than fetching once in onMounted — works on both login and refresh.
let loadedForCompanyId: string | null = null;
watch(
  () => companyStore.companyId,
  (companyId) => {
    if (!companyId || !hasSelectedCompany() || loadedForCompanyId === companyId) return;
    loadedForCompanyId = companyId;
    fetchRecent();
  },
  { immediate: true }
);
</script>

<template>
  <div class="h-full border border-neautrals-200 rounded-md bg-white flex flex-col">
    <div class="flex p-4 items-center justify-between shadow">
      <span class="mr-2">{{ t('dashboard.Activity') }}</span>
      <router-link
        :to="{ name: 'activity-log', query: { mode: 'detailed' } }"
        class="flex items-center"
      >
        <span class="mr-1 underline font-medium text-xs">{{
          t('dashboard.goIntoDetail', 'See detail')
        }}</span>
        <i class="ri-arrow-right-s-line"></i>
      </router-link>
    </div>

    <div class="flex-1 p-3 space-y-2 overflow-y-auto max-h-[360px]">
      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-10 text-neautrals-400">
        <i class="ri-loader-4-line animate-spin text-2xl"></i>
      </div>

      <!-- Empty -->
      <div
        v-else-if="events.length === 0"
        class="flex flex-col items-center justify-center py-10 text-neautrals-400"
      >
        <i class="ri-inbox-line text-3xl mb-2"></i>
        <span class="text-xs">{{ t('activityLog.empty', 'No activity yet') }}</span>
      </div>

      <!-- Cards -->
      <template v-else>
        <div
          v-for="card in cards"
          :key="card.id"
          class="flex items-start gap-3 p-3 rounded-md border border-neautrals-200 hover:bg-neautrals-50 transition-colors"
        >
          <div
            :class="[
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              card.bg,
            ]"
          >
            <i :class="[card.icon, card.color, 'text-sm']"></i>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-neautrals-900 leading-snug">
              {{ card.operation }}
            </p>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xxs text-neautrals-500">
              <span class="flex items-center">
                <i class="ri-user-line mr-1"></i>{{ card.user }}
              </span>
              <span class="flex items-center" :title="card.exact">
                <i class="ri-time-line mr-1"></i>{{ card.relative }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
